/**
 * Owner: Masa Bayu
 * File: capability.ts
 * Date:Time: 2026-09-22 06:02
 * Description: PWA-Phase-A capability detection — DETECTABLE ≠ PRODUCT-CLAIMED.
 */

export interface PwaCapabilitySnapshot {
  readonly secureContext: boolean;
  readonly serviceWorker: boolean;
  readonly beforeInstallPrompt: boolean;
  readonly standalone: boolean;
  readonly notification: boolean;
  readonly pushManager: boolean;
  readonly share: boolean;
  readonly indexedDb: boolean;
  /** Product claim: elevated PWA features require secure context. */
  readonly canElevatePwa: boolean;
}

export function detectPwaCapabilities(
  input: {
    readonly isSecureContext?: boolean;
    readonly serviceWorker?: boolean;
    readonly standaloneMedia?: boolean;
    readonly notification?: boolean;
    readonly pushManager?: boolean;
    readonly share?: boolean;
    readonly indexedDb?: boolean;
    /** True when a BIP event has been observed this session. */
    readonly beforeInstallPromptSeen?: boolean;
  } = {},
): PwaCapabilitySnapshot {
  const secureContext =
    input.isSecureContext ??
    (typeof window !== 'undefined' ? window.isSecureContext === true : false);
  const serviceWorker =
    input.serviceWorker ??
    (typeof navigator !== 'undefined' && 'serviceWorker' in navigator);
  const standalone =
    input.standaloneMedia ??
    (typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches);
  const notification =
    input.notification ?? (typeof window !== 'undefined' && 'Notification' in window);
  const pushManager =
    input.pushManager ??
    (typeof window !== 'undefined' && 'PushManager' in window);
  const share =
    input.share ??
    (typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  const indexedDb =
    input.indexedDb ?? (typeof indexedDB !== 'undefined');
  const beforeInstallPrompt =
    input.beforeInstallPromptSeen === true;

  return Object.freeze({
    secureContext,
    serviceWorker,
    beforeInstallPrompt,
    standalone,
    notification,
    pushManager,
    share,
    indexedDb,
    canElevatePwa: secureContext && serviceWorker,
  });
}
